import { MotionPlayIcon, SyncDesktopIcon } from '@/components/icons'
import { SortableTableHead } from '@/components/projects/SortableTableHead'
import { TriggerWorkflowDialog } from '@/components/projects/workflows/TriggerWorkflowDialog'
import { TriggerWorkflowFromEntityDialog, type TriggerableDefinition } from '@/components/projects/workflows/TriggerWorkflowFromEntityDialog'
import { MemberAvatar } from '@/components/projects/MemberAvatar'
import { WorkItemStatusBadge } from '@/components/projects/WorkItemStatusBadge'
import { Checkbox } from '@/components/ui/checkbox'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getErrorMessage } from '@/api/errors'
import { useUpdateWorkItemStatus, useWorkItem } from '@/hooks'
import { useAuth } from '@/hooks/useAuth'
import { Permissions } from '@/lib/permissions'
import { extractValidationMessage } from '@/lib/validationErrors'
import type { WorkItem } from '@/types'
import { normalizeWorkItemStatus } from '@/types'
import { ExternalLink } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { AssignWorkItemDialog } from './AssignWorkItemDialog'
import { DeleteWorkItemDialog } from './DeleteWorkItemDialog'
import { EditWorkItemDialog } from './EditWorkItemDialog'
import { PushToDevOpsDialog } from './PushToDevOpsDialog'
import { WorkItemActionsMenu } from './WorkItemActionsMenu'
import { WorkItemDetailDialog } from './WorkItemDetailDialog'
import { WORK_ITEM_DEFAULT_SORT_DIRECTIONS, type WorkItemSortColumn } from '@/types/workItem'
import { formatWorkItemNumber } from '@/lib/workItemFormat'

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString()
}

function assigneeLabel(workItem: WorkItem): string | null {
  if (!workItem.assignedToUserId) return null
  return workItem.assignedToDisplayName || workItem.assignedToEmail || null
}

interface BacklogTableProps {
  workItems: WorkItem[]
  projectId: string
  selectionMode: boolean
  selectedIds: Set<string>
  onToggleRow: (id: string) => void
  onToggleAll: (ids: string[]) => void
  azureDevOpsConnected: boolean
  showExternalIdColumn: boolean
  sortBy: WorkItemSortColumn
  sortDirection: 'asc' | 'desc'
  onSortChange: (column: WorkItemSortColumn) => void
}

export function BacklogTable({
  workItems,
  projectId,
  selectionMode,
  selectedIds,
  onToggleRow,
  onToggleAll,
  azureDevOpsConnected,
  showExternalIdColumn,
  sortBy,
  sortDirection,
  onSortChange,
}: BacklogTableProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedWorkItemId = searchParams.get('workItemId')
  const { data: selectedWorkItemFromApi } = useWorkItem(projectId, selectedWorkItemId)

  const selectedWorkItem = useMemo(
    () =>
      workItems.find((wi) => wi.id === selectedWorkItemId) ??
      selectedWorkItemFromApi ??
      null,
    [workItems, selectedWorkItemId, selectedWorkItemFromApi],
  )

  const [editingWorkItem, setEditingWorkItem] = useState<WorkItem | null>(null)
  const [deletingWorkItem, setDeletingWorkItem] = useState<WorkItem | null>(null)
  const [assigningWorkItem, setAssigningWorkItem] = useState<WorkItem | null>(null)
  const [triggeringWorkItemId, setTriggeringWorkItemId] = useState<string | null>(null)
  const [triggerPickerOpen, setTriggerPickerOpen] = useState(false)
  const [triggerDefinition, setTriggerDefinition] = useState<TriggerableDefinition | null>(null)
  const [pushingWorkItem, setPushingWorkItem] = useState<WorkItem | null>(null)
  const [statusUpdatingIds, setStatusUpdatingIds] = useState<Set<string>>(new Set())

  const { hasPermission } = useAuth(projectId)
  const canTrigger = hasPermission(Permissions.Project.Workflow.Trigger, projectId)
  const canPush = hasPermission(Permissions.Project.WorkItem.Update, projectId)
  const canChangeStatus = hasPermission(Permissions.Project.WorkItem.Update, projectId)
  const canAssign = hasPermission(Permissions.Project.WorkItem.Update, projectId)
  const canComplete = hasPermission(Permissions.Project.WorkItem.Complete, projectId)
  const canDelete = hasPermission(Permissions.Project.WorkItem.Delete, projectId)

  const updateWorkItemStatus = useUpdateWorkItemStatus(projectId)

  const handleChangeStatus = async (workItem: WorkItem, status: string) => {
    if (statusUpdatingIds.has(workItem.id)) return

    setStatusUpdatingIds(prev => new Set(prev).add(workItem.id))
    try {
      await updateWorkItemStatus.mutateAsync({
        id: workItem.id,
        data: { status, expectedStatus: normalizeWorkItemStatus(workItem.status) ?? workItem.status },
      })
      toast.success(`Status updated to ${status}`)
    } catch (error) {
      toast.error(
        extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to update status',
      )
    } finally {
      setStatusUpdatingIds(prev => {
        const next = new Set(prev)
        next.delete(workItem.id)
        return next
      })
    }
  }

  const allVisibleIds = workItems.map(w => w.id)
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every(id => selectedIds.has(id))
  const someSelected = allVisibleIds.some(id => selectedIds.has(id))

  const setSelectedWorkItemId = (workItemId: string | null, options?: { replace?: boolean }) => {
    const next = new URLSearchParams(searchParams)
    if (workItemId) next.set('workItemId', workItemId)
    else next.delete('workItemId')
    setSearchParams(next, { replace: options?.replace ?? false })
  }

  const pushTooltip = !azureDevOpsConnected ? 'Connect Azure DevOps to enable push' : undefined

  return (
    <div data-testid="backlog-table">
      <Table aria-label="Work items">
        <TableHeader>
          <TableRow>
            {selectionMode && (
              <TableHead className="w-[48px]">
                <Checkbox
                  aria-label="Select all visible work items"
                  checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                  onCheckedChange={() => onToggleAll(allVisibleIds)}
                />
              </TableHead>
            )}
            <SortableTableHead
              column="number"
              label="#"
              sortBy={sortBy}
              sortDirection={sortDirection}
              defaultDirections={WORK_ITEM_DEFAULT_SORT_DIRECTIONS}
              onSortChange={onSortChange}
              className="w-[70px]"
            />
            <SortableTableHead
              column="title"
              label="Title"
              sortBy={sortBy}
              sortDirection={sortDirection}
              defaultDirections={WORK_ITEM_DEFAULT_SORT_DIRECTIONS}
              onSortChange={onSortChange}
            />
            {showExternalIdColumn && (
              <SortableTableHead
                column="externalId"
                label="External ID"
                sortBy={sortBy}
                sortDirection={sortDirection}
                defaultDirections={WORK_ITEM_DEFAULT_SORT_DIRECTIONS}
                onSortChange={onSortChange}
                className="w-[140px]"
              />
            )}
            <SortableTableHead
              column="epic"
              label="Epic"
              sortBy={sortBy}
              sortDirection={sortDirection}
              defaultDirections={WORK_ITEM_DEFAULT_SORT_DIRECTIONS}
              onSortChange={onSortChange}
              className="w-[120px]"
            />
            <SortableTableHead
              column="feature"
              label="Feature"
              sortBy={sortBy}
              sortDirection={sortDirection}
              defaultDirections={WORK_ITEM_DEFAULT_SORT_DIRECTIONS}
              onSortChange={onSortChange}
              className="w-[135px]"
            />
            <TableHead className="w-[155px]">Assigned To</TableHead>
            <SortableTableHead
              column="status"
              label="Status"
              sortBy={sortBy}
              sortDirection={sortDirection}
              defaultDirections={WORK_ITEM_DEFAULT_SORT_DIRECTIONS}
              onSortChange={onSortChange}
              className="w-[140px]"
            />
            <SortableTableHead
              column="created"
              label="Created"
              sortBy={sortBy}
              sortDirection={sortDirection}
              defaultDirections={WORK_ITEM_DEFAULT_SORT_DIRECTIONS}
              onSortChange={onSortChange}
              className="w-[100px]"
            />
            {!selectionMode && <TableHead className="w-[150px]">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {workItems.map((workItem) => {
            const isChecked = selectedIds.has(workItem.id)
            return (
              <TableRow
                key={workItem.id}
                className={`cursor-pointer ${isChecked ? 'bg-accent/40' : ''}`}
                onClick={() => {
                  if (selectionMode) onToggleRow(workItem.id)
                  else setSelectedWorkItemId(workItem.id)
                }}
              >
                {selectionMode && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      aria-label={`Select work item ${formatWorkItemNumber(workItem.number)}`}
                      checked={isChecked}
                      onCheckedChange={() => onToggleRow(workItem.id)}
                    />
                  </TableCell>
                )}
                <TableCell className="font-medium text-muted-foreground">
                  {formatWorkItemNumber(workItem.number)}
                </TableCell>
                <TableCell>
                  <span className="font-medium" data-testid="work-item-title">{workItem.title}</span>
                </TableCell>
                {showExternalIdColumn && (
                  <TableCell data-testid="work-item-external-id">
                    {workItem.externalSource === 'AzureDevOps' && workItem.externalUrl ? (
                      <a
                        href={workItem.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 rounded-full rounded bg-external-id-badge border border-external-id-badge-border px-2 py-0.5 text-xs font-medium text-external-id-badge-foreground"
                        title={`Linked to Azure DevOps work item #${workItem.externalId}`}
                      >
                        ADO #{workItem.externalId}
                        <ExternalLink className="h-4 w-4 text-foreground" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                )}
                <TableCell>{workItem.epic ?? ''}</TableCell>
                <TableCell>{workItem.feature ?? ''}</TableCell>
                <TableCell>
                  {assigneeLabel(workItem) ? (
                    <div className="flex items-center gap-2" data-testid="work-item-assignee">
                      <MemberAvatar name={assigneeLabel(workItem)!} className="h-7 w-7 shrink-0" />
                      <span className="min-w-0">{assigneeLabel(workItem)}</span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground" data-testid="work-item-assignee">—</span>
                  )}
                </TableCell>
                <TableCell>
                  <WorkItemStatusBadge status={workItem.status} />
                </TableCell>
                <TableCell className="text-foreground">
                  {formatDate(workItem.createdAt)}
                </TableCell>
                {!selectionMode && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <TooltipProvider>
                        {canTrigger && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                className="shrink-0 p-1.5 rounded-lg text-action-icon hover:bg-muted focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                onClick={() => { setTriggeringWorkItemId(workItem.id); setTriggerPickerOpen(true) }}
                                aria-label={`Trigger workflow for work item ${formatWorkItemNumber(workItem.number)}`}
                              >
                                <MotionPlayIcon className="h-5 w-5" />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>Trigger workflow</TooltipContent>
                          </Tooltip>
                        )}
                        {canPush && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex">
                                <button
                                  type="button"
                                  className="shrink-0 p-1.5 rounded-lg text-action-icon hover:bg-muted focus:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                                  onClick={() => setPushingWorkItem(workItem)}
                                  disabled={!azureDevOpsConnected}
                                  aria-label={`Push work item ${formatWorkItemNumber(workItem.number)} to Azure DevOps`}
                                >
                                  <SyncDesktopIcon className="h-5 w-5" />
                                </button>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>{pushTooltip ?? 'Push to Azure DevOps'}</TooltipContent>
                          </Tooltip>
                        )}
                      </TooltipProvider>
                      <WorkItemActionsMenu
                        workItem={workItem}
                        workItemLabel={`work item ${formatWorkItemNumber(workItem.number)}`}
                        canChangeStatus={canChangeStatus}
                        canComplete={canComplete}
                        canAssign={canAssign}
                        canDelete={canDelete}
                        isPending={statusUpdatingIds.has(workItem.id)}
                        onChangeStatus={(status) => handleChangeStatus(workItem, status)}
                        onAssign={() => setAssigningWorkItem(workItem)}
                        onDelete={() => setDeletingWorkItem(workItem)}
                      />
                    </div>
                  </TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>

      <WorkItemDetailDialog
        workItem={selectedWorkItem}
        projectId={projectId}
        open={selectedWorkItem !== null}
        onOpenChange={(open) => { if (!open) setSelectedWorkItemId(null, { replace: true }) }}
        onEdit={() => {
          if (selectedWorkItem) {
            setEditingWorkItem(selectedWorkItem)
            setSelectedWorkItemId(null, { replace: true })
          }
        }}
      />

      {editingWorkItem && (
        <EditWorkItemDialog
          projectId={projectId}
          workItem={editingWorkItem}
          open={editingWorkItem !== null}
          onOpenChange={(open) => { if (!open) setEditingWorkItem(null) }}
        />
      )}

      {assigningWorkItem && (
        <AssignWorkItemDialog
          projectId={projectId}
          workItem={assigningWorkItem}
          open={assigningWorkItem !== null}
          onOpenChange={(open) => { if (!open) setAssigningWorkItem(null) }}
        />
      )}

      {deletingWorkItem && (
        <DeleteWorkItemDialog
          projectId={projectId}
          workItem={deletingWorkItem}
          open={deletingWorkItem !== null}
          onOpenChange={(open) => { if (!open) setDeletingWorkItem(null) }}
        />
      )}

      {pushingWorkItem && (
        <PushToDevOpsDialog
          projectId={projectId}
          workItems={[pushingWorkItem]}
          open={pushingWorkItem !== null}
          onOpenChange={(open) => { if (!open) setPushingWorkItem(null) }}
        />
      )}

      <TriggerWorkflowFromEntityDialog
        projectId={projectId}
        open={triggerPickerOpen}
        onOpenChange={setTriggerPickerOpen}
        onSelect={(def) => setTriggerDefinition(def)}
        linkedEntityType="WorkItem"
        linkedEntityId={triggeringWorkItemId ?? undefined}
      />

      <TriggerWorkflowDialog
        projectId={projectId}
        definition={triggerDefinition}
        open={triggerDefinition !== null}
        onOpenChange={(open) => { if (!open) setTriggerDefinition(null) }}
        extraInputValues={triggeringWorkItemId ? { workItemId: triggeringWorkItemId } : undefined}
        linkedEntityId={triggeringWorkItemId ?? undefined}
        linkedEntityType="WorkItem"
      />
    </div>
  )
}
